import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, User, Upload, Pencil, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CrewManagementPage() {
  const { fleetId } = useParams();
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(null);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);
  const [newCrew, setNewCrew] = useState({ name: '', rank: '', email: '', phone: '', notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [fleetRes, crewRes] = await Promise.all([
        axios.get(`${API_URL}/api/fleets/${fleetId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/crew-members?fleet_id=${fleetId}`, { withCredentials: true })
      ]);
      setFleet(fleetRes.data);
      setCrew(crewRes.data);
    } catch (error) {
      toast.error('Failed to load crew data');
    } finally {
      setLoading(false);
    }
  }, [fleetId, setFleet, setCrew, setLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateCrew = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/crew-members`, { ...newCrew, fleet_id: fleetId }, { withCredentials: true });
      toast.success('Crew member added');
      setNewCrew({ name: '', rank: '', email: '', phone: '', notes: '' });
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add crew member');
    }
  };

  const handleUpdateCrew = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/crew-members/${editingCrew.id}`, editingCrew, { withCredentials: true });
      toast.success('Crew member updated');
      setEditingCrew(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update crew member');
    }
  };

  const handleDeleteCrew = async (crewId) => {
    if (!window.confirm('Delete this crew member?')) return;
    try {
      await axios.delete(`${API_URL}/api/crew-members/${crewId}`, { withCredentials: true });
      toast.success('Crew member deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete crew member');
    }
  };

  const handlePhotoUpload = async (crewId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingPhotoFor(crewId);
    try {
      const { data } = await axios.post(`${API_URL}/api/photos/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await axios.put(`${API_URL}/api/crew-members/${crewId}/photo?photo_id=${data.id}`, {}, { withCredentials: true });
      toast.success('Photo uploaded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhotoFor(null);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/fleets')} className="p-2 hover:bg-gray-100 rounded-sm">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-[#111827]">{fleet?.name} - Crew Management</h1>
            <p className="text-sm text-[#6B7280] mt-1">{fleet?.description}</p>
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a]">
            <Plus size={20} /> Add Crew Member
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {crew.map((member) => (
            <div key={member.id} className="bg-white border border-[#E5E7EB] rounded-sm p-4">
              <div className="flex items-start gap-4">
                <div className="relative">
                  {member.photo_id ? (
                    <img src={`${API_URL}/api/photos/${member.photo_id}`} alt={member.name} className="w-16 h-16 rounded-full object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-[#E5E7EB] flex items-center justify-center">
                      <User size={32} className="text-[#9CA3AF]" />
                    </div>
                  )}
                  <label className="absolute -bottom-1 -right-1 p-1 bg-[#002FA7] text-white rounded-full cursor-pointer hover:bg-[#001f7a]">
                    <Upload size={14} />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(member.id, e.target.files[0])} disabled={uploadingPhotoFor === member.id} />
                  </label>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-[#111827] truncate">{member.name}</h3>
                  <p className="text-sm text-[#6B7280]">{member.rank}</p>
                  {member.email && <p className="text-xs text-[#9CA3AF] mt-1 truncate">{member.email}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditingCrew(member)} className="p-1 text-[#6B7280] hover:text-[#002FA7] rounded-sm">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => handleDeleteCrew(member.id)} className="p-1 text-[#6B7280] hover:text-[#FF2A2A] rounded-sm">
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              {member.notes && <p className="text-xs text-[#6B7280] mt-2 line-clamp-2">{member.notes}</p>}
            </div>
          ))}
        </div>

        {crew.length === 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-sm p-12 text-center">
            <User size={64} className="mx-auto text-[#9CA3AF] mb-4" weight="duotone" />
            <h3 className="text-lg font-semibold text-[#111827] mb-2">No Crew Members</h3>
            <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-[#002FA7] text-white rounded-sm font-medium hover:bg-[#001f7a]">Add First Crew Member</button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#111827] mb-4">Add Crew Member</h3>
            <form onSubmit={handleCreateCrew}>
              <input type="text" placeholder="Name" value={newCrew.name} onChange={(e) => setNewCrew({...newCrew, name: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="text" placeholder="Rank" value={newCrew.rank} onChange={(e) => setNewCrew({...newCrew, rank: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="email" placeholder="Email (optional)" value={newCrew.email} onChange={(e) => setNewCrew({...newCrew, email: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" />
              <input type="tel" placeholder="Phone (optional)" value={newCrew.phone} onChange={(e) => setNewCrew({...newCrew, phone: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" />
              <textarea placeholder="Notes (optional)" value={newCrew.notes} onChange={(e) => setNewCrew({...newCrew, notes: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-4" rows={2} />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 border border-[#E5E7EB] text-[#4B5563] rounded-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#002FA7] text-white rounded-sm">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingCrew && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#111827] mb-4">Edit Crew Member</h3>
            <form onSubmit={handleUpdateCrew}>
              <input type="text" value={editingCrew.name} onChange={(e) => setEditingCrew({...editingCrew, name: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="text" value={editingCrew.rank} onChange={(e) => setEditingCrew({...editingCrew, rank: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" required />
              <input type="email" value={editingCrew.email || ''} onChange={(e) => setEditingCrew({...editingCrew, email: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" />
              <input type="tel" value={editingCrew.phone || ''} onChange={(e) => setEditingCrew({...editingCrew, phone: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-3" />
              <textarea value={editingCrew.notes || ''} onChange={(e) => setEditingCrew({...editingCrew, notes: e.target.value})} className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm mb-4" rows={2} />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditingCrew(null)} className="px-4 py-2 border border-[#E5E7EB] text-[#4B5563] rounded-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#002FA7] text-white rounded-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}